// Root-route dispatcher. Probes OPFS once on mount to decide whether
// this is a first-time install (no primary vault yet → /setup) or a
// returning-user state (primary vault present → /master).
//
// Renders a minimal loading placeholder until the probe resolves —
// avoids a brief mis-route to /master on a fresh install before the
// probe completes.

import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useVault } from "../../hooks/useVault";

type Probe = { kind: "loading" } | { kind: "fresh" } | { kind: "returning" };

export function RootRedirect() {
  const vault = useVault();
  const [probe, setProbe] = useState<Probe>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    vault
      .opfsVaultExists("")
      .then((present) => {
        if (cancelled) return;
        setProbe({ kind: present ? "returning" : "fresh" });
      })
      .catch(() => {
        // Worker unreachable or OPFS broken — fall through to /setup
        // so the user can recover by re-running first-time setup.
        if (cancelled) return;
        setProbe({ kind: "fresh" });
      });
    return () => {
      cancelled = true;
    };
  }, [vault]);

  if (probe.kind === "loading") {
    return (
      <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 text-sm text-text-soft md:px-6 md:py-12">
        Loading…
      </div>
    );
  }
  if (probe.kind === "fresh") {
    return <Navigate to="/setup" replace />;
  }
  return <Navigate to="/master" replace />;
}
